import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const explainerSlides = [
  "Track your patient's daily health.",
  "Log symptoms, hydration, and medication.",
  "Record critical events calmly and privately.",
  "Share with Doctors or Family Members"
];

const Login = ({ setUser }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showPricing, setShowPricing] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setSendingReset(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      toast.success('If an account exists with this email, you will receive a password reset link.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setSendingReset(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % explainerSlides.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? `${API}/auth/login` : `${API}/auth/register`;
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(endpoint, payload);
      const { access_token, user } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #FAFAFA 0%, #F0F9F8 100%)' }}>
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl flex gap-8 items-center">
          {/* Left Panel - Explainer Slides */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="hidden lg:flex flex-1 flex-col gap-6"
          >
            <div className="text-center mb-4">
              <h2 className="text-4xl font-bold mb-2" style={{ color: '#333' }}>Welcome to Annie Cares</h2>
              <p className="text-lg italic text-gray-600 mb-2">Because caring for someone shouldn't feel overwhelming.</p>
              <p className="text-gray-700">Track symptoms, medication, and important moments. All in one gentle space.</p>
            </div>
            
            <div className="card" style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f9f8', border: '2px solid #A2DAD3' }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="text-center px-8"
                >
                  <p className="text-2xl font-medium" style={{ color: '#A2DAD3' }}>
                    {explainerSlides[currentSlide]}
                  </p>
                  <div className="flex gap-2 justify-center mt-6">
                    {explainerSlides.map((_, index) => (
                      <div
                        key={index}
                        className="h-2 rounded-full transition-all duration-300"
                        style={{
                          width: currentSlide === index ? '32px' : '8px',
                          backgroundColor: currentSlide === index ? '#A2DAD3' : '#d0d0d0'
                        }}
                      />
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Pricing Preview */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card p-6"
              style={{ backgroundColor: '#fff', border: '2px solid #A2DAD3' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Crown size={24} style={{ color: '#A2DAD3' }} />
                <h3 className="text-lg font-semibold">Simple Pricing</h3>
              </div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-gray-700 font-medium">Start free with one patient</p>
                  <p className="text-sm text-gray-500">14-day free trial of all premium features</p>
                </div>
                <div className="text-right">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold" style={{ color: '#A2DAD3' }}>$5</span>
                    <span className="text-gray-500">/mo</span>
                  </div>
                  <p className="text-xs text-gray-500">or $39/year</p>
                </div>
              </div>
              <button
                onClick={() => setShowPricing(true)}
                className="text-sm font-medium hover:underline"
                style={{ color: '#A2DAD3' }}
              >
                View full pricing details
              </button>
            </motion.div>
          </motion.div>

          {/* Right Panel - Login Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full lg:w-auto lg:flex-1 max-w-md"
          >
            <div className="card">
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_caretrack-27/artifacts/7k11ovbc_annie-cares-logo.jpg" 
                    alt="Annie Cares Logo" 
                    className="w-32 h-32 object-contain"
                  />
                </div>
                {/* Mobile-only heading */}
                <div className="lg:hidden">
                  <h1 className="text-3xl font-bold mb-2" style={{ color: '#333' }}>Welcome to Annie Cares</h1>
                  <p className="text-sm italic text-gray-600 mb-1">Because caring for someone shouldn't feel overwhelming.</p>
                  <p className="text-xs text-gray-700 mb-4">Track symptoms, medication, and important moments.</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} data-testid="auth-form">
                {!isLogin && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <input
                      data-testid="name-input"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required={!isLogin}
                      placeholder="Your name"
                    />
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    data-testid="email-input"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <input
                    data-testid="password-input"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                  />
                </div>

                <button
                  data-testid="submit-button"
                  type="submit"
                  className="btn btn-primary w-full mb-4"
                  disabled={loading}
                >
                  {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Sign Up'}
                </button>
              </form>

              <div className="text-center space-y-2">
                <button
                  data-testid="toggle-auth-button"
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-sm block w-full"
                  style={{ color: '#A2DAD3' }}
                >
                  {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                </button>
                {isLogin && (
                  <button
                    data-testid="forgot-password-button"
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Forgot your password?
                  </button>
                )}
              </div>

              {/* Mobile Pricing Preview */}
              <div className="lg:hidden mt-6 pt-6 border-t">
                <button
                  onClick={() => setShowPricing(true)}
                  className="w-full p-4 rounded-xl flex items-center justify-between"
                  style={{ backgroundColor: '#f0f9f8', border: '2px solid #A2DAD3' }}
                >
                  <div className="flex items-center gap-3">
                    <Crown size={20} style={{ color: '#A2DAD3' }} />
                    <span className="font-medium text-sm">Free to start, upgrade anytime</span>
                  </div>
                  <span className="text-xs font-bold" style={{ color: '#A2DAD3' }}>$5/mo</span>
                </button>
              </div>

              {/* Terms and Privacy Links */}
              <div className="mt-6 pt-4 border-t text-center text-xs text-gray-500">
                <p>By signing up, you agree to our</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <a href="/terms" className="hover:underline" style={{ color: '#A2DAD3' }}>Terms & Conditions</a>
                  <span>and</span>
                  <a href="/privacy" className="hover:underline" style={{ color: '#A2DAD3' }}>Privacy Policy</a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowForgotPassword(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-md w-full p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Reset Password</h2>
              <button 
                onClick={() => setShowForgotPassword(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Enter your email address and we'll send you a link to reset your password.
              </p>
              
              <input
                type="email"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="w-full"
              />

              <button
                onClick={handleForgotPassword}
                disabled={sendingReset}
                className="btn btn-primary w-full"
              >
                {sendingReset ? 'Sending...' : 'Send Reset Link'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Pricing Modal */}
      {showPricing && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowPricing(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Pricing Plans</h2>
              <button 
                onClick={() => setShowPricing(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={24} />
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Free Plan */}
              <div className="border-2 rounded-xl p-5" style={{ borderColor: '#e5e5e5' }}>
                <h3 className="text-xl font-bold mb-2">Free Plan</h3>
                <div className="text-3xl font-bold mb-4">$0<span className="text-lg text-gray-500">/forever</span></div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    <span>1 patient profile</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    <span>Track symptoms</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    <span>Blood pressure & sugar logs</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    <span>Medication tracking</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    <span>Export reports</span>
                  </div>
                </div>
              </div>

              {/* Premium Plan */}
              <div className="border-2 rounded-xl p-5 relative" style={{ borderColor: '#A2DAD3' }}>
                <div className="absolute -top-3 right-4 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: '#A2DAD3' }}>
                  14-day trial
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Crown size={20} style={{ color: '#A2DAD3' }} />
                  <h3 className="text-xl font-bold">Premium</h3>
                </div>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-3xl font-bold">$5</span>
                  <span className="text-gray-500">/month</span>
                  <span className="text-sm text-gray-500">or $39/year</span>
                </div>
                
                <p className="text-sm font-medium mb-3" style={{ color: '#A2DAD3' }}>Everything in Free, plus:</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    <span className="font-medium">Unlimited patients</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    <span className="font-medium">Doctor appointments</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    <span className="font-medium">Food restrictions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    <span className="font-medium">Advanced analytics</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    <span className="font-medium">Priority support</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 rounded-xl bg-gray-50 text-center">
              <p className="text-gray-600 text-sm">
                Sign up today and get a <strong>14-day free trial</strong> of all premium features!
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Login;
